import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { checkActiveUser } from '../store/slices/authSlice'
import {
  fetchInstallmentsByPlotId,
  fetchPaidInstallmentsForUser,
  fetchOverdueInstallments,
  fetchActiveInstallmentsByBooking,
  fetchResellInstallmentsByBooking
} from '../store/slices/installmentSlice'
import {
  BookCopy,
  HandCoins,
  ReceiptText,
  Sparkles,
  X,
  ChevronRight,
  Calendar,
  CheckCircle2,
  Clock,
  AlertTriangle,
  FileText,
  Printer,
  Banknote,
  CreditCard,
  Home,
  User,
  ChevronDown,
  ChevronUp,
  CheckCircle
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import Modal from 'react-modal'
import CompletedInstallmentsPrint from './util/CompletedInstallmentsPaid'
import InstallmentBookingSlip from './util/InstallmentBookingPaid'
import { getAllBookings } from '../store/slices/bookingSlice'
import { fetchSocieties } from '../store/slices/societySlice'
import { useSidebarContext } from '../scenes/global/sidebar/SidebarContext'
import {
          fetchUpcomingPayments,
          fetchOverdueAmount,
          fetchPaymentSchedule,
          downloadReceiptPDF,
          fetchCustomerDocuments,
          downloadDocumentPDF,
          uploadCustomerDocuments,
          fetchCustomerDashboardSummary
        } from '../api'

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  backdropFilter: 'blur(12px)',
  padding: '1rem'
}

const modalStyle = {
  position: 'relative',
  background: 'white',
  borderRadius: '16px',
  padding: '0', // Full bleed for the header
  width: '100%',
  maxWidth: '1000px', // Wider for dashboard data
  maxHeight: '90vh',
  overflow: 'hidden', // Let the component handle internal scrolling
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
  zIndex: 1001,
  border: 'none',
  inset: 'auto' // Center it better with flexbox in overlay
}

const printStyles = `
  @media print {
    .print-btn, .close-btn, .nav-sidebar {
      display: none !important;
    }
    body {
      background: white !important;
      color: black !important;
    }
    .print-container {
      padding: 0;
      margin: 0;
      background: white !important;
    }
  }
  
  /* Custom scrollbar for webkit browsers */
  .custom-scrollbar::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 8px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(79, 209, 197, 0.5);
    border-radius: 8px;
  }
  
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(79, 209, 197, 0.7);
  }
  
  /* Glow animations */
  @keyframes glow {
    0% { box-shadow: 0 0 10px rgba(79, 209, 197, 0.3); }
    50% { box-shadow: 0 0 25px rgba(79, 209, 197, 0.6); }
    100% { box-shadow: 0 0 10px rgba(79, 209, 197, 0.3); }
  }
  
  @keyframes float {
    0% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
    100% { transform: translateY(0px); }
  }
  
  .glow-effect {
    animation: glow 4s infinite ease-in-out;
  }
  
  .float-effect {
    animation: float 6s infinite ease-in-out;
  }
  
  /* 3D card styles */
  .card-3d-container {
    perspective: 1500px;
  }
  
  .card-3d {
    transition: transform 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    transform-style: preserve-3d;
  }
  
  .card-3d:hover {
    transform: rotateX(5deg) rotateY(5deg);
  }
  
  /* Animated gradient backgrounds */
  .animated-gradient {
    background: linear-gradient(-45deg, #0f172a, #1e293b, #0f766e, #134e4a);
    background-size: 400% 400%;
    animation: gradient 15s ease infinite;
  }
  
  @keyframes gradient {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  .mesh-grid {
    background-image: 
      radial-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
      radial-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px);
    background-size: 30px 30px;
    background-position: 0 0, 15px 15px;
  }
`

const PaidInstallments = () => {
  const dispatch = useDispatch()
  const { activeUser, isAuthenticated, error } = useSelector(
    state => state.auth
  )
  const { bookings: allBookings = [] } = useSelector(state => state.booking)

  const {
    activeInstallments,
    resellCurrentOwnerInstallments,
    resellPreviousOwnerInstallments,
    paidInstallments,
    installments,
    error: installmentsError
  } = useSelector(state => state.installments)
  // console.log('Installments:', installments)
  const [totals, setTotals] = useState({
    totalPaidAmount: 0,
    totalPendingInstallmentsAmount: 0,
    totalPlotAmount: 0
  })
  const { societies } = useSelector(state => state.societies)

  const [selectedPlot, setSelectedPlot] = useState(null)
  const [isSlipModalOpen, setIsSlipModalOpen] = useState(false)
  const [selectedInstallment, setSelectedInstallment] = useState(null)
  const [selectedInstallments, setSelectedInstallments] = useState([])
  const { gradient, theme } = useSidebarContext()
  const [currentView, setCurrentView] = useState('all')
  const [
    isCompletedInstallmentsPrintModalOpen,
    setIsCompletedInstallmentsPrintModalOpen
  ] = useState(false)
  const [isHeroExpanded, setIsHeroExpanded] = useState(false)
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
  const [scrollProgress, setScrollProgress] = useState(0)
  const [upcomingPayments, setUpcomingPayments] = useState([])
  const [overdueData, setOverdueData] = useState(null)
  const [paymentSchedule, setPaymentSchedule] = useState([])
  const [customerDocuments, setCustomerDocuments] = useState(null)
  // P1: Document Upload State
  const [selectedFiles, setSelectedFiles] = useState([])
  const [uploadingDocuments, setUploadingDocuments] = useState(false)
  // Download All Receipts State
  const [downloadingAllReceipts, setDownloadingAllReceipts] = useState(false)
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 })
  // ISSUE 1: Customer Dashboard Summary State
  const [dashboardSummary, setDashboardSummary] = useState({
    totalPlots: 0,
    totalPaid: 0,
    totalPayable: 0,
    overdueAmount: 0,
    nextInstallment: null
  })

  // Aggregate and filter installments from different sources (exclude cancelled)
  const combinedInstallments = useMemo(() => {
    const allInstallmentsArray = [
      ...(activeInstallments && Array.isArray(activeInstallments) ? activeInstallments.filter(inst => inst.status !== 'Cancelled') : []),
      ...(resellCurrentOwnerInstallments && Array.isArray(resellCurrentOwnerInstallments) ? resellCurrentOwnerInstallments.filter(inst => inst.status !== 'Cancelled') : []),
      ...(resellPreviousOwnerInstallments && Array.isArray(resellPreviousOwnerInstallments) ? resellPreviousOwnerInstallments.filter(inst => inst.status !== 'Cancelled') : []),
      ...(paidInstallments && Array.isArray(paidInstallments) ? paidInstallments.filter(inst => inst.status !== 'Cancelled') : [])
    ];
    
    // Get the user's Customer ID (not User ID)
    // activeUser.id might be User ID, but we need Customer ID from customerData
    const userCustomerId = activeUser?.customerData?._id?.toString() || activeUser?.customerData?.toString() || activeUser?.id?.toString();
    
    // Get user's booking IDs and plot IDs for filtering transferred installments
    const userBookingIds = new Set(
      allBookings
        .filter(booking => {
          const bookingCustomerId = (booking.customer_id?._id || booking.customer_id)?.toString();
          return bookingCustomerId === userCustomerId;
        })
        .map(booking => booking._id?.toString())
        .filter(Boolean)
    );
    
    const userPlotIds = new Set(
      allBookings
        .filter(booking => {
          const bookingCustomerId = (booking.customer_id?._id || booking.customer_id)?.toString();
          return bookingCustomerId === userCustomerId;
        })
        .map(booking => {
          const plotId = booking.plot_id?._id || booking.plot_id;
          return plotId?.toString();
        })
        .filter(Boolean)
    );
    
    // Filter installments: Include user's own installments AND transferred installments for their bookings/plots
    // The backend already filters by booking, so we just need to ensure we're not filtering out transferred ones
    const filtered = allInstallmentsArray.filter(installment => {
      const customerId = (installment.customer_id?._id || installment.customer_id)?.toString();
      const plotId = (installment.plot_id?._id || installment.plot_id)?.toString();
      const currentBookingId = (installment.current_booking_id?._id || installment.current_booking_id)?.toString();
      
      // Include if:
      // 1. Installment belongs to the user (normal case) - customer_id matches user's Customer ID
      // 2. OR it's a transferred installment that belongs to the user's booking (resell case)
      //    - status is "Transferred" 
      //    - transfer_type is "resell"
      //    - current_booking_id matches one of the user's bookings (most reliable check)
      //    - OR plot_id matches one of the user's plots (fallback)
      const belongsToUser = customerId === userCustomerId;
      const isTransferredForUserBooking = 
        installment.status === 'Transferred' && 
        installment.transfer_type === 'resell' &&
        currentBookingId &&
        userBookingIds.has(currentBookingId);
      const isTransferredForUserPlot = 
        installment.status === 'Transferred' && 
        installment.transfer_type === 'resell' &&
        plotId && 
        userPlotIds.has(plotId);
      
      const shouldInclude = belongsToUser || isTransferredForUserBooking || isTransferredForUserPlot;
      
      return shouldInclude;
    });
    
    // Debug: Log summary for troubleshooting
    const transferredCount = allInstallmentsArray.filter(i => i.status === 'Transferred' && i.transfer_type === 'resell').length;
    const includedTransferredCount = filtered.filter(i => i.status === 'Transferred' && i.transfer_type === 'resell').length;
    if (transferredCount > 0 || includedTransferredCount > 0) {
      console.log('DEBUG: Transferred installments filter:', {
        totalTransferred: transferredCount,
        includedTransferred: includedTransferredCount,
        userCustomerId,
        userBookingIds: Array.from(userBookingIds),
        userPlotIds: Array.from(userPlotIds),
        allBookingsCount: allBookings.length
      });
    }
    
    return filtered;
  }, [activeInstallments, resellCurrentOwnerInstallments, resellPreviousOwnerInstallments, paidInstallments, activeUser, allBookings]);

  // Deduplicate installments by _id
  const allInstallments = useMemo(() => combinedInstallments.filter((installment, index, self) =>
    index === self.findIndex(i => i._id === installment._id)
  ), [combinedInstallments]);

  // Log duplicate detection
  if (combinedInstallments.length !== allInstallments.length) {
    console.log('DEBUG: PaidInstallments - Filtered duplicates:', combinedInstallments.length - allInstallments.length, 'duplicates removed');
  }

  // Compute user plots including completed ones from bookings and installments
  const userPlots = useMemo(() => {
    if (!activeUser) return [];
    // 1. First, harvest all unique plots from all sources
    const plotInfoMap = new Map();

    // Helper to merge plot details
    const mergePlotData = (id, data) => {
      const existing = plotInfoMap.get(id) || {};
      plotInfoMap.set(id, {
        ...existing,
        ...data,
        _id: id // Ensure ID is preserved
      });
    };

    // Source A: activeUser.plots (Usually contains full plot metadata)
    if (activeUser.plots && Array.isArray(activeUser.plots)) {
      activeUser.plots.forEach(p => mergePlotData(p._id?.toString(), p));
    }

    // Source B: allBookings (Context for ownership and active booking status)
    if (allBookings && Array.isArray(allBookings)) {
      allBookings.forEach(booking => {
        const plot = booking.plot_id;
        if (plot) {
          const plotId = (plot._id || plot).toString();
          const plotData = typeof plot === 'object' ? plot : {};
          mergePlotData(plotId, { ...plotData, booking });
        }
      });
    }

    // Source C: allInstallments (Fallback for missing plots)
    if (allInstallments && Array.isArray(allInstallments)) {
      allInstallments.forEach(inst => {
        const plot = inst.plot_id;
        if (plot) {
          const plotId = (plot._id || plot).toString();
          const plotData = typeof plot === 'object' ? plot : {};
          mergePlotData(plotId, plotData);
        }
      });
    }

    // Now return the merged array but only for plots the user actually owns/is linked to
    return Array.from(plotInfoMap.values()).filter(plot => {
      const hasBooking = allBookings?.some(b => (b.plot_id?._id || b.plot_id)?.toString() === plot._id?.toString());
      const isInUserPlots = activeUser.plots?.some(p => p._id?.toString() === plot._id?.toString());
      return hasBooking || isInUserPlots;
    }).map(plot => {
      // Robust society lookup
      const society = societies?.find(soc =>
        soc.plots?.some(p => (p._id || p).toString() === plot._id?.toString()) ||
        soc._id?.toString() === plot.society_id?.toString()
      ) || {
        name: 'Society',
        location: 'N/A',
        society_image: null
      };

      return {
        ...plot,
        marla: plot.marla || plot.size, // Normalize Marla/Size
        soc: society
      };
    });
  }, [activeUser, allBookings, allInstallments, societies]);

  // Card refs for 3D effect
  const cardRefs = useRef({})

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  }

  const plotCardVariants = {
    initial: { scale: 0.95, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    hover: { scale: 1.03, transition: { duration: 0.3 } }
  }

  const staggeredContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const staggeredItemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  }

  // Handle mouse movement for 3D card effects
  const handleMouseMove = (e, id) => {
    if (!cardRefs.current[id]) return

    const card = cardRefs.current[id]
    const rect = card.getBoundingClientRect()

    // Calculate position relative to center
    const xPos = (e.clientX - rect.left) / rect.width - 0.5
    const yPos = (e.clientY - rect.top) / rect.height - 0.5

    // Apply transform
    card.style.transform = `
      perspective(1000px)
      rotateY(${xPos * 10}deg)
      rotateX(${yPos * -10}deg)
      translateZ(10px)
    `
  }

  const handleMouseLeave = id => {
    if (!cardRefs.current[id]) return

    // Reset transform when mouse leaves
    cardRefs.current[id].style.transform = `
      perspective(1000px)
      rotateY(0deg)
      rotateX(0deg)
      translateZ(0px)
    `
  }

  // Track page scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY
      const height = document.documentElement.scrollHeight - window.innerHeight
      const progress = scrollY / height
      setScrollProgress(progress)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Track mouse position for global effects
  useEffect(() => {
    const handleMouseMove = e => {
      setMousePosition({ x: e.clientX, y: e.clientY })
    }

    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(checkActiveUser())
    }
  }, [dispatch, isAuthenticated])

  useEffect(() => {
    console.log('DEBUG: PaidInstallments - Initial data loading useEffect triggered');

    // Load bookings - for customers, load their bookings; for admins, load all bookings
    const customerId = activeUser?.role === 'Customer' ? activeUser.id : null;

    dispatch(getAllBookings({ customerId }))
      .unwrap()
      .then(data => {
        console.log('DEBUG: PaidInstallments - Bookings loaded:', data.bookings?.length || 0, 'bookings for', customerId ? 'customer' : 'admin');
      })
      .catch(error => {
        console.error('DEBUG: PaidInstallments - Failed to load bookings:', error);
      });

    dispatch(fetchSocieties());
    dispatch(fetchOverdueInstallments());

    // ISSUE 1: Load customer dashboard data using dedicated endpoint
    if (activeUser?.role === 'Customer') {
      // Fetch dashboard summary (replaces local calculations)
      fetchCustomerDashboardSummary()
        .then(data => {
          setDashboardSummary({
            totalPlots: data?.totalPlots || 0,
            totalPaid: data?.totalPaid || 0,
            totalPayable: data?.totalPayable || 0,
            overdueAmount: data?.overdueAmount || 0,
            nextInstallment: data?.nextInstallment || null
          });
        })
        .catch(error => {
          console.error('Error fetching customer dashboard summary:', error);
          // Set defaults on error
          setDashboardSummary({
            totalPlots: 0,
            totalPaid: 0,
            totalPayable: 0,
            overdueAmount: 0,
            nextInstallment: null
          });
        });

      fetchUpcomingPayments()
        .then(data => setUpcomingPayments(data || []))
        .catch(error => console.error('Error fetching upcoming payments:', error));

      fetchOverdueAmount()
        .then(data => setOverdueData(data))
        .catch(error => console.error('Error fetching overdue amount:', error));

      fetchPaymentSchedule()
        .then(data => setPaymentSchedule(data || []))
        .catch(error => console.error('Error fetching payment schedule:', error));
    }
  }, [dispatch, activeUser])

  useEffect(() => {
    if (isAuthenticated && activeUser && activeUser.id && userPlots.length > 0) {
      userPlots.forEach(plot => {
        const booking = plot.booking;
        const plotId = plot._id?.toString();

        if (booking) {
          // Check if we already have these installments in our list to avoid infinite fetching
          const existingInstallments = allInstallments.filter(
            inst => (inst.plot_id?._id || inst.plot_id)?.toString() === plotId
          );

          const updateTotalsWithData = (data) => {
            const completedInstallments = data.filter(inst => inst.status === 'Completed');
            const totalPaid = completedInstallments.reduce((sum, inst) => sum + (inst.paid_amount || 0), 0);
            const initialPayment = booking.initial_payment || 0;
            const totalPlotAmount = booking.total_amount || 0;
            const totalPaidIncludingInitial = totalPaid + initialPayment;
            const isSold = booking.booking_status === 'Sold' || booking.booking_status === 'Completed';

            setTotals(prevTotals => ({
              ...prevTotals,
              [plotId]: {
                totalPaidAmount: isSold ? totalPlotAmount : totalPaidIncludingInitial,
                totalPendingInstallmentsAmount: isSold ? 0 : Math.max(0, totalPlotAmount - totalPaidIncludingInitial),
                totalPlotAmount: totalPlotAmount,
                actualPaidAmount: totalPaidIncludingInitial,
                initialPayment: initialPayment,
                bookingStatus: booking.booking_status,
                category: booking.plot_id?.category || plot.category,
                marla: booking.plot_id?.marla || booking.plot_id?.size || plot.marla || plot.size,
                bookingDate: booking.booking_date
              }
            }));
          };

          if (existingInstallments.length > 0) {
            // We already have some data, calculate totals locally first
            updateTotalsWithData(existingInstallments);
          } else {
            // No data in store, fetch it
            console.log('DEBUG: PaidInstallments - Fetching data for booking:', booking._id);
            dispatch(fetchActiveInstallmentsByBooking(booking._id))
              .unwrap()
              .then(installments => {
                updateTotalsWithData(installments);
              })
              .catch(error => {
                console.error('Error fetching active installments:', error);
              });
          }
        }
      });
    }
  }, [dispatch, isAuthenticated, activeUser?.id, userPlots, allInstallments]);


  // Auto-select first plot when user data is available
  useEffect(() => {
    if (isAuthenticated && activeUser && userPlots.length > 0 && !selectedPlot) {
      console.log('DEBUG: PaidInstallments - Auto-selecting first plot');
      const firstPlot = userPlots[0];
      setSelectedPlot(firstPlot);
      console.log('DEBUG: PaidInstallments - First plot selected:', firstPlot);
    }
  }, [isAuthenticated, activeUser, societies, selectedPlot]);

  const handleViewSlip = installment => {
    setSelectedInstallment(installment)
    setSelectedInstallments([installment])
    setIsSlipModalOpen(true)

    // For print compatibility, also set an enriched version
    const enriched = {
      ...installment,
      remaining_amount: installment.amount - (installment.paid_amount || 0)
    }
    // Update selectedInstallment with corrected remaining_amount
    setSelectedInstallment(enriched)
  }

  const handleCloseModal = () => {
    setIsSlipModalOpen(false)
    setSelectedInstallment(null)
  }

  const enrichedInstallments = selectedInstallments.map(installment => {
    const customer = installment.customer_id || {}

    // Recalculate remaining_amount to ensure it's correct (never negative)
    const correctRemainingAmount = Math.max(0, installment.amount - (installment.paid_amount || 0))

    // Ensure customer data is properly populated for print
    const customerData = (installment.customer_id && installment.customer_id.name) ? installment.customer_id :
      (customer && customer.name) ? customer :
        { name: 'Customer', contactInfo: { phone: 'N/A', address: 'N/A', cnic: 'N/A' } }

    return {
      ...installment,
      remaining_amount: correctRemainingAmount, // Fix remaining amount for print
      customer_id: customerData, // Ensure customer data is available
      plotDetails: {
        plotNumber: selectedPlot?.plot_number || 'N/A',
        plotType: selectedPlot?.plot_type || 'N/A',
        block: selectedPlot?.block || 'N/A',
        size: selectedPlot?.size || 'N/A'
      },
      customerDetails: {
        name: customerData?.name || 'N/A',
        phone: customerData?.contactInfo?.phone || 'N/A',
        cnic: customerData?.contactInfo?.cnic || 'N/A',
        address: customerData?.contactInfo?.address || 'N/A'
      }
    }
  })

  const handlePlotClick = async (plotId) => {
    const plot = userPlots.find(plot => plot._id?.toString() === plotId)

    if (plot) {
      setSelectedPlot(plot)

      // Check if we already have installments for this plot
      let plotInstallments = allInstallments.filter(
        installment =>
          ((installment.plot_id?._id || installment.plot_id)?.toString() === plot._id?.toString())
      )

      // Find booking for this plot
      const plotBooking = allBookings.find(
        booking => booking.plot_id?._id?.toString() === plot._id?.toString()
      )

      if (!plotBooking) {
        console.log('DEBUG: No booking found for plot:', plot._id, 'Available bookings:', allBookings.length)
        // Try to find any booking that might match
        const possibleBookings = allBookings.filter(
          booking => booking.customer_id?._id?.toString() === activeUser?.id
        )
        console.log('DEBUG: User bookings count:', possibleBookings.length)
      }

      // If no installments loaded for this plot and we have a booking, try to fetch them
      if (plotInstallments.length === 0 && plotBooking) {
        console.log('DEBUG: PaidInstallments - No installments found for plot, fetching from booking:', plotId, plotBooking._id)
        try {
          // Fetch all installments for this booking
          const bookingData = await dispatch(fetchActiveInstallmentsByBooking(plotBooking._id)).unwrap()
          plotInstallments = bookingData || []
          console.log('DEBUG: PaidInstallments - Fetched installments for plot:', plotId, 'Count:', plotInstallments.length)
        } catch (error) {
          console.error('DEBUG: PaidInstallments - Failed to fetch installments for plot:', plotId, error)
          // Continue with empty array
          plotInstallments = []
        }
      } else if (plotInstallments.length === 0 && !plotBooking) {
        console.log('DEBUG: PaidInstallments - No booking found for plot:', plotId, '- cannot fetch installments')
      }

      // Calculate totals
      const completedInstallments = plotInstallments.filter(inst => inst.status === 'Completed')
      const pendingInstallments = plotInstallments.filter(inst =>
        ['Pending', 'Partially Paid', 'Overdue'].includes(inst.status)
      )

      const totalPaidFromInstallments = completedInstallments.reduce(
        (sum, inst) => sum + (inst.paid_amount || 0), 0
      )
      const initialPayment = plotBooking?.initial_payment || 0
      const totalPaidAmount = totalPaidFromInstallments + initialPayment

      const totalPendingAmount = pendingInstallments.reduce(
        (sum, inst) => sum + (inst.amount || 0), 0
      )

      // Calculate total plot amount
      let totalPlotAmount = plotBooking?.total_amount || 0

      // If booking doesn't have total_amount, calculate from installments
      if (totalPlotAmount === 0) {
        // Sum all installment amounts for this plot + initial payment
        const totalFromInstallments = plotInstallments.reduce(
          (sum, inst) => sum + (inst.amount || 0), 0
        )
        totalPlotAmount = totalFromInstallments + initialPayment
      }

      // For fully paid plots, ensure total matches what was actually paid
      if (totalPendingAmount === 0 && totalPaidAmount > 0 && totalPlotAmount !== totalPaidAmount) {
        console.log('DEBUG: Adjusting totalPlotAmount for fully paid plot:', totalPlotAmount, '->', totalPaidAmount)
        totalPlotAmount = totalPaidAmount
      }

      // Ensure totalPlotAmount is never zero to avoid division by zero
      totalPlotAmount = Math.max(totalPlotAmount, 1)

      // Update local totals state
      setTotals(prevTotals => ({
        ...prevTotals,
        [plotId]: {
          totalPaidAmount,
          totalPendingInstallmentsAmount: totalPendingAmount,
          totalPlotAmount,
          initialPayment: initialPayment
        }
      }));

      const hasPending = plotInstallments.some(inst => ['Pending', 'Partially Paid', 'Overdue'].includes(inst.status))
      const hasCompleted = plotInstallments.some(inst => inst.status === 'Completed')

      if (!hasPending && hasCompleted) {
        setCurrentView('completed')
      } else if (hasPending) {
        setCurrentView('pending')
      } else {
        setCurrentView('all')
      }

      console.log('DEBUG: PaidInstallments - Plot selected:', plotId, 'Society found:', !!plot.soc)
      console.log('DEBUG: PaidInstallments - Plot booking found:', !!plotBooking, 'Booking ID:', plotBooking?._id)
      console.log('DEBUG: PaidInstallments - Plot installments:', plotInstallments.length, 'Completed:', completedInstallments.length, 'Pending:', pendingInstallments.length)
      console.log('DEBUG: PaidInstallments - Booking total_amount:', plotBooking?.total_amount, 'Calculated total:', totalPlotAmount)
      console.log('DEBUG: PaidInstallments - Calculated totals - Paid:', totalPaidAmount, 'Pending:', totalPendingAmount, 'Total:', totalPlotAmount)
      console.log('DEBUG: PaidInstallments - Initial payment:', initialPayment, 'Auto-view:', hasPending ? 'pending' : hasCompleted ? 'completed' : 'all')
    }
  }

  if (error) {
    return (
      <div
        className={`flex items-center justify-center h-screen ${theme.tailwind.bg8}`}
      >
        <div className='text-red-400 bg-red-400/10 px-8 py-6 rounded-2xl backdrop-blur-xl border border-red-400/20 shadow-xl shadow-red-500/10'>
          <div className='flex items-center gap-4'>
            <AlertTriangle size={28} />
            <div>
              <h3 className='font-bold text-xl mb-1'>Error</h3>
              <p>{error}</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!activeUser) {
    return (
      <div
        className={`flex items-center justify-center h-screen ${theme.tailwind.bg8}`}
      >
        <div
          className={`${theme.tailwind.text9} ${theme.tailwind.bg55} px-8 py-6 rounded-2xl backdrop-blur-xl border ${theme.tailwind.border24} shadow-xl ${theme.tailwind.shadowhover10} animate-pulse`}
        >
          <div className='flex items-center gap-4'>
            <User size={28} />
            <div>
              <h3 className='font-bold text-xl mb-1'>
                Authentication Required
              </h3>
              <p>No user data available. Please sign in to continue.</p>
            </div>
          </div>
        </div>
      </div>
    )
  }


  // Find the booking for the selected plot
  const selectedPlotBooking = allBookings.find(
    booking => (booking.plot_id?._id || booking.plot_id)?.toString() === selectedPlot?._id?.toString() &&
               (booking.customer_id?._id || booking.customer_id)?.toString() === (activeUser?.customerData?._id || activeUser?.customerData || activeUser?.id)?.toString()
  )
  const selectedPlotBookingId = selectedPlotBooking?._id?.toString()

  // Filter installments for selected plot:
  // 1. Normal installments: plot_id matches selected plot
  // 2. Transferred installments: current_booking_id matches the booking for selected plot (for resold plots)
  const filteredInstallments = allInstallments.filter(installment => {
    const installmentPlotId = (installment.plot_id?._id || installment.plot_id)?.toString()
    const installmentCurrentBookingId = (installment.current_booking_id?._id || installment.current_booking_id)?.toString()
    const matchesPlot = installmentPlotId === selectedPlot?._id?.toString()
    const isTransferredForThisBooking = 
      installment.status === 'Transferred' && 
      installment.transfer_type === 'resell' &&
      installmentCurrentBookingId &&
      selectedPlotBookingId &&
      installmentCurrentBookingId === selectedPlotBookingId
    
    return (matchesPlot || isTransferredForThisBooking) && 
           ['Pending', 'Partially Paid', 'Overdue'].includes(installment.status)
  })

  // Get completed installments for selected plot (including transferred ones)
  const completeInstallments = allInstallments.filter(installment => {
    const installmentPlotId = (installment.plot_id?._id || installment.plot_id)?.toString()
    const installmentCurrentBookingId = (installment.current_booking_id?._id || installment.current_booking_id)?.toString()
    const matchesPlot = installmentPlotId === selectedPlot?._id?.toString()
    const isTransferredForThisBooking = 
      installment.status === 'Transferred' && 
      installment.transfer_type === 'resell' &&
      installmentCurrentBookingId &&
      selectedPlotBookingId &&
      installmentCurrentBookingId === selectedPlotBookingId
    
    return (matchesPlot || isTransferredForThisBooking) && 
           (installment.status === 'Completed' || installment.status === 'Transferred')
  })

  console.log('DEBUG: PaidInstallments - selectedPlot:', selectedPlot?._id);
  console.log('DEBUG: PaidInstallments - allInstallments count:', allInstallments.length);
  console.log('DEBUG: PaidInstallments - completeInstallments count:', completeInstallments.length);
  console.log('DEBUG: PaidInstallments - filteredInstallments count:', filteredInstallments.length);

  // Debug plot ID comparison
  if (allInstallments.length > 0 && selectedPlot) {
    console.log('DEBUG: Sample installment plot_id:', allInstallments[0].plot_id);
    console.log('DEBUG: Sample installment plot_id type:', typeof allInstallments[0].plot_id);
    console.log('DEBUG: Sample installment plot_id._id:', allInstallments[0].plot_id?._id);
    console.log('DEBUG: selectedPlot._id:', selectedPlot._id);
    console.log('DEBUG: Comparison result:', (allInstallments[0].plot_id?._id || allInstallments[0].plot_id)?.toString() === selectedPlot._id?.toString());
  }

  // Debug installment statuses
  const statusCounts = allInstallments.reduce((acc, inst) => {
    acc[inst.status] = (acc[inst.status] || 0) + 1;
    return acc;
  }, {});
  console.log('DEBUG: PaidInstallments - status counts:', statusCounts);
  console.log('DEBUG: PaidInstallments - sample installment plot_id:', allInstallments[0]?.plot_id);

  const getStatusColor = status => {
    switch (status) {
      case 'Pending':
        return 'text-amber-400 bg-amber-400/10 border-amber-400/20'
      case 'Partially Paid':
        return 'text-blue-400 bg-blue-400/10 border-blue-400/20'
      case 'Overdue':
        return 'text-red-400 bg-red-400/10 border-red-400/20'
      case 'Completed':
        return 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20'
      default:
        return `${theme.tailwind.text} ${theme.tailwind.bg58} ${theme.tailwind.border24}`
    }
  }

  const getStatusIcon = status => {
    switch (status) {
      case 'Pending':
        return <Clock className='w-4 h-4' />
      case 'Partially Paid':
        return <AlertTriangle className='w-4 h-4' />
      case 'Overdue':
        return <AlertTriangle className='w-4 h-4' />
      case 'Completed':
        return <CheckCircle2 className='w-4 h-4' />
      default:
        return <Clock className='w-4 h-4' />
    }
  }

  const completionPercentage = selectedPlot
    ? (() => {
      const totalPaid = totals[selectedPlot._id]?.totalPaidAmount || 0
      const totalAmount = totals[selectedPlot._id]?.totalPlotAmount || 0
      // Avoid division by zero and ensure valid percentage
      return totalAmount > 0 ? (totalPaid / totalAmount) * 100 : (totalPaid > 0 ? 100 : 0)
    })()
    : 0

  // Debug logging for progress calculation
  console.log('DEBUG: Progress calculation for plot:', selectedPlot?._id)
  console.log('DEBUG: Totals for plot:', totals[selectedPlot?._id])
  console.log('DEBUG: Completion percentage:', completionPercentage)

  return (
    <div
      className={`min-h-screen ${gradient} ${theme.tailwind.text9} flex rounded-xl shadow-lg`}
    >
      <div className='absolute inset-0 overflow-hidden pointer-events-none'>
        <div className='absolute inset-0 opacity-30 mesh-grid'></div>

        <div
          className={`absolute -top-96 -left-96 w-192 h-192 rounded-full ${theme.tailwind.bg10} blur-3xl animate-pulse`}
        ></div>
        <div
          className={`absolute -bottom-96 -right-96 w-192 h-192 rounded-full ${theme.tailwind.bg10} blur-3xl animate-pulse delay-1000`}
        ></div>
        <div
          className={`absolute top-1/4 right-1/4 w-96 h-96 rounded-full ${theme.tailwind.bgEx1} blur-3xl animate-pulse delay-2000`}
        ></div>

        <div className='absolute inset-0 bg-noise opacity-5'></div>
      </div>

      {/* Main content */}
      <div className='flex-1'>
        <div className='p-6 md:p-8 lg:p-12'>
          {activeUser && activeUser.role === 'Customer' && (
            <div className='space-y-8'>
              {/* SECTION 1: Top - Next Payment & Status */}
              {(upcomingPayments.length > 0 || overdueData?.totalOverdue > 0) && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`rounded-2xl p-6 border ${
                    overdueData?.totalOverdue > 0 
                      ? 'bg-gradient-to-r from-red-500/20 via-orange-500/20 to-red-500/20 border-red-500/30'
                      : 'bg-gradient-to-r from-amber-500/20 via-yellow-500/20 to-amber-500/20 border-amber-500/30'
                  }`}
                >
                  <div className='flex items-center gap-3 mb-4'>
                    <div className={`p-2 rounded-lg ${
                      overdueData?.totalOverdue > 0 
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-amber-500/20 text-amber-400'
                    }`}>
                      <AlertTriangle size={24} />
                    </div>
                    <h3 className='text-xl font-bold text-white'>Action Required</h3>
                  </div>
                  <div className='space-y-3'>
                    {upcomingPayments.length > 0 && upcomingPayments[0]?.due_date && (
                      <div className='flex items-center justify-between p-3 bg-white/5 rounded-lg'>
                        <div className='flex items-center gap-3'>
                          <Calendar size={18} className='text-amber-400' />
                          <span className='text-white font-medium'>
                            Payment Due in {Math.max(0, Math.ceil((new Date(upcomingPayments[0].due_date) - new Date()) / (1000 * 60 * 60 * 24)))} Days
                          </span>
                        </div>
                        <span className='text-amber-400 font-bold'>
                          Rs. {Math.max(0, ((upcomingPayments[0]?.amount || 0) - (upcomingPayments[0]?.paid_amount || 0))).toLocaleString()}
                        </span>
                      </div>
                    )}
                    {overdueData?.totalOverdue > 0 && (
                      <div className='flex items-center justify-between p-3 bg-red-500/10 rounded-lg border border-red-500/30'>
                        <div className='flex items-center gap-3'>
                          <AlertTriangle size={18} className='text-red-400' />
                          <span className='text-white font-medium'>Overdue Amount</span>
                        </div>
                        <div className='text-right'>
                          <span className='text-red-400 font-bold block'>
                            Rs. {overdueData.totalOverdue.toLocaleString()}
                          </span>
                          {overdueData.lateFee > 0 && (
                            <span className='text-red-300 text-sm'>
                              Late Fee: Rs. {overdueData.lateFee.toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* SECTION 2: Middle - Payment Progress */}
              <motion.div
                className={`relative rounded-3xl overflow-hidden transition-all duration-500 ease-in-out ${isHeroExpanded ? 'h-96' : 'h-72'
                  }`}
                initial='hidden'
                animate='visible'
                variants={fadeIn}
              >
                <div className='absolute inset-0 z-0'>
                  <div
                    className={`absolute inset-0 ${theme.tailwind.bgr39}`}
                  ></div>
                  <div
                    className='absolute inset-0 perspective-scene'
                    style={{
                      background: `linear-gradient(rgba(0,0,0,0) 0%, rgba(0,0,0,0) 80%, rgba(79, 209, 197, 0.15) 100%)`,
                      backgroundSize: '30px 30px',
                      backgroundPosition: `${mousePosition.x / 50}px ${mousePosition.y / 50
                        }px`,
                      perspective: '1000px',
                      transformStyle: 'preserve-3d'
                    }}
                  >
                    <div
                      className='absolute inset-0'
                      style={{
                        backgroundImage: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(79, 209, 197, 0.4) 0%, transparent 60%)`
                      }}
                    ></div>
                  </div>

                  {/* Animated grid lines */}
                  <div
                    className='absolute inset-0'
                    style={{
                      backgroundImage: `
                        linear-gradient(to right, rgba(79, 209, 197, 0.1) 1px, transparent 1px),
                        linear-gradient(to bottom, rgba(79, 209, 197, 0.1) 1px, transparent 1px)
                      `,
                      backgroundSize: '40px 40px',
                      transform: `translateZ(20px) rotateX(60deg) translateY(-100px) translateX(${mousePosition.x / 30
                        }px)`
                    }}
                  ></div>

                  <div
                    className={`absolute -left-1/4 -top-1/4 w-1/2 h-1/2 ${theme.tailwind.bg25} rounded-full blur-3xl animate-pulse`}
                  ></div>
                  <div
                    className={`absolute -right-1/4 -bottom-1/4 w-1/2 h-1/2 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000`}
                  ></div>
                </div>

                <div className='absolute inset-0 flex flex-col justify-center px-8 md:px-12 lg:px-16 z-10'>
                  <motion.div
                    className='max-w-5xl backdrop-blur-xl bg-white/5 rounded-3xl border border-white/10 shadow-2xl p-8 md:p-10 overflow-hidden'
                    whileHover={{ scale: 1.01 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  >
                    <div className='flex flex-col md:flex-row justify-between items-start md:items-center gap-6'>
                      <div className='flex items-center gap-6'>
                        <div className='relative hidden md:block float-effect'>
                          <div className={`absolute inset-0 rounded-full ${theme.tailwind.bgr40} blur-lg opacity-75 animate-pulse`}></div>
                          <div className={`relative h-20 w-20 rounded-full ${theme.tailwind.bgr41} flex items-center justify-center border border-white/20 shadow-xl`}>
                            <div className='absolute inset-0 rounded-full bg-gradient-to-r from-transparent to-black/30'></div>
                            <Sparkles className={`w-10 h-10 ${theme.tailwind.text9} animate-pulse`} />
                          </div>
                        </div>

                        <div className='space-y-3'>
                          <motion.h1
                            className='text-3xl md:text-5xl font-black tracking-tight text-white'
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                          >
                            Welcome back, <span className='text-amber-500'>{activeUser.username}</span>
                          </motion.h1>
                          <motion.p
                            className={`text-lg ${theme.tailwind.texting1} opacity-80`}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                          >
                            Property <span className="font-bold">Dashboard</span> • Track your investments effortlessly.
                          </motion.p>
                        </div>
                      </div>

                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                      >
                        <button
                          onClick={() => setIsCompletedInstallmentsPrintModalOpen(true)}
                          disabled={!selectedPlot}
                          className={`px-6 py-3 rounded-xl transition-all flex items-center gap-3 font-bold shadow-lg
                            ${selectedPlot
                              ? `bg-gradient-to-r from-amber-500 to-amber-600 text-white shadow-amber-500/20 hover:shadow-amber-500/40 transform hover:-translate-y-0.5`
                              : `bg-white/5 text-white/20 cursor-not-allowed`
                            }`}
                        >
                          <Printer size={18} />
                          <span>Print Summary</span>
                        </button>
                      </motion.div>
                    </div>

                    <AnimatePresence>
                      {isHeroExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className='mt-8 pt-8 border-t border-white/10'
                        >
                          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'>
                            {/* ISSUE 1: Use dashboard summary data from API */}
                            <div className='bg-white/5 rounded-2xl p-5 border border-white/10'>
                              <p className='text-white/40 text-[10px] font-bold uppercase mb-2 tracking-widest'>Total Assets</p>
                              <p className='text-2xl font-black text-white'>{dashboardSummary.totalPlots || 0} Plots</p>
                            </div>
                            <div className='bg-white/5 rounded-2xl p-5 border border-white/10'>
                              <p className='text-white/40 text-[10px] font-bold uppercase mb-2 tracking-widest'>Total Invested</p>
                              <p className='text-2xl font-black text-emerald-400'>Rs. {(dashboardSummary.totalPaid || 0).toLocaleString()}</p>
                            </div>
                            <div className='bg-white/5 rounded-2xl p-5 border border-white/10'>
                              <p className='text-white/40 text-[10px] font-bold uppercase mb-2 tracking-widest'>Total Payable</p>
                              <p className='text-2xl font-black text-amber-400'>Rs. {(dashboardSummary.totalPayable || 0).toLocaleString()}</p>
                            </div>
                            <div className='bg-white/5 rounded-2xl p-5 border border-white/10'>
                              <p className='text-white/40 text-[10px] font-bold uppercase mb-2 tracking-widest'>Overall Progress</p>
                              <p className='text-2xl font-black text-blue-400'>
                                {(dashboardSummary.totalPaid || 0) + (dashboardSummary.totalPayable || 0) > 0 
                                  ? Math.round(((dashboardSummary.totalPaid || 0) / ((dashboardSummary.totalPaid || 0) + (dashboardSummary.totalPayable || 0))) * 100) 
                                  : 0}%
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <button
                      onClick={() => setIsHeroExpanded(!isHeroExpanded)}
                      className='absolute bottom-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors border border-white/10'
                    >
                      {isHeroExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </motion.div>
                </div>
              </motion.div>

              {/* Properties Selection Section */}
              <section>
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className='text-2xl font-black text-white'>My Properties</h2>
                    <p className='text-white/40'>Select a property to view payment schedules and history</p>
                  </div>
                </div>

                <motion.div
                  className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
                  variants={staggeredContainerVariants}
                  initial='hidden'
                  animate='visible'
                >
                  {userPlots.map(plot => {
                    const isSelected = selectedPlot && selectedPlot._id === plot._id;
                    const plotTotals = totals[plot._id] || {};
                    const progress = plotTotals.totalPlotAmount > 0
                      ? Math.min(100, Math.round((plotTotals.totalPaidAmount / plotTotals.totalPlotAmount) * 100))
                      : (plotTotals.totalPaidAmount > 0 ? 100 : 0);

                    const status = plotTotals.bookingStatus || plot.booking_status || 'Ongoing';
                    const isSold = status === 'Sold' || status === 'Completed';

                    return (
                      <motion.div
                        key={plot._id}
                        ref={el => (cardRefs.current[plot._id] = el)}
                        className={`group relative rounded-3xl p-px overflow-hidden cursor-pointer transition-all duration-500 ${isSelected ? 'ring-2 ring-amber-500 shadow-2xl shadow-amber-500/20 scale-[1.02]' : 'hover:shadow-xl'
                          }`}
                        variants={staggeredItemVariants}
                        onClick={() => handlePlotClick(plot._id)}
                        onMouseMove={e => handleMouseMove(e, plot._id)}
                        onMouseLeave={() => handleMouseLeave(plot._id)}
                      >
                        <div className={`relative h-full ${theme.tailwind.BgBottom6} border border-white/10 rounded-[23px] p-6 flex flex-col z-10 overflow-hidden`}>
                          <div className='flex justify-between items-start mb-6'>
                            <div className={`p-3 rounded-2xl ${isSelected ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30' : 'bg-white/5 text-white/50 group-hover:bg-white/10 transition-colors'}`}>
                              <Home size={24} />
                            </div>
                            <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${isSold ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
                              }`}>
                              {status === 'Booked' ? 'Ongoing' : status}
                            </span>
                          </div>

                          <div className='mb-6'>
                            <h3 className='text-2xl font-black text-white mb-1'>
                              Plot {plot.plot_number}
                            </h3>
                            <p className='text-white/40 text-sm font-medium'>
                              {plot.block || 'N/A'} Block • {plotTotals.marla || plot.marla || plot.size || 'N/A'} Marla
                            </p>
                          </div>

                          <div className='grid grid-cols-2 gap-4 mb-8'>
                            <div className='bg-white/5 p-3 rounded-2xl border border-white/5'>
                              <p className='text-white/20 text-[10px] font-bold uppercase mb-1 tracking-wider'>Category</p>
                              <p className='text-white text-xs font-bold'>{plotTotals.category || plot.category || 'General'}</p>
                            </div>
                            <div className='bg-white/5 p-3 rounded-2xl border border-white/5'>
                              <p className='text-white/20 text-[10px] font-bold uppercase mb-1 tracking-wider'>Type</p>
                              <p className='text-white text-xs font-bold'>{plot.plot_type || 'Residential'}</p>
                            </div>
                          </div>

                          <div className='mt-auto space-y-4'>
                            <div className='space-y-2'>
                              <div className='flex justify-between text-xs font-bold'>
                                <span className='text-white/40'>Total Value</span>
                                <span className='text-white'>Rs. {(plotTotals.totalPlotAmount || 0).toLocaleString()}</span>
                              </div>
                              <div className='flex justify-between text-xs font-bold'>
                                <span className='text-white/40'>Total Paid</span>
                                <span className='text-emerald-400'>Rs. {(plotTotals.totalPaidAmount || 0).toLocaleString()}</span>
                              </div>
                            </div>

                            <div className='pt-2'>
                              <div className='flex justify-between text-[10px] font-black uppercase tracking-widest text-white/40 mb-2'>
                                <span>Progress</span>
                                <span className={isSold ? 'text-emerald-400' : 'text-amber-400'}>{isSold ? 100 : progress}%</span>
                              </div>
                              <div className='h-2 w-full bg-white/5 rounded-full overflow-hidden border border-white/5'>
                                <motion.div
                                  className={`h-full ${isSold ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-gradient-to-r from-amber-500 to-orange-500'}`}
                                  initial={{ width: 0 }}
                                  animate={{ width: `${isSold ? 100 : progress}%` }}
                                  transition={{ duration: 1, delay: 0.5 }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )
                  })}
                </motion.div>
              </section>

              {selectedPlot && (
                <section className='mt-12 space-y-8 animate-fadeIn'>
                  <div className='flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6'>
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${(totals[selectedPlot._id]?.bookingStatus || selectedPlot.booking_status) === 'Sold' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'
                          }`}>
                          {totals[selectedPlot._id]?.bookingStatus || selectedPlot.booking_status || 'Ongoing'}
                        </span>
                        <span className="text-white/20">•</span>
                        <span className="text-white/40 text-sm font-bold uppercase tracking-widest">Plot {selectedPlot.plot_number}</span>
                      </div>
                      <h2 className='text-4xl font-black text-white'>
                        Installment Records
                      </h2>
                    </div>

                    <div className={`flex bg-white/5 backdrop-blur-md rounded-2xl p-1.5 border border-white/10 shadow-xl`}>
                      {['all', 'pending', 'completed'].map((view) => (
                        <button
                          key={view}
                          className={`px-6 py-2.5 rounded-xl text-sm font-black transition-all duration-300 uppercase tracking-wider ${currentView === view
                            ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/20'
                            : 'text-white/40 hover:text-white'
                            }`}
                          onClick={() => setCurrentView(view)}
                        >
                          {view}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Booking Overview Card */}
                  <div className={`${theme.tailwind.BgBottom6} backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl relative overflow-hidden group`}>
                    <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/5 blur-3xl rounded-full -mr-32 -mt-32"></div>

                    <div className='relative z-10'>
                      <div className='flex items-center gap-4 mb-8 pb-8 border-b border-white/5'>
                        <div className="p-4 rounded-2xl bg-amber-500/10 text-amber-500">
                          <FileText size={32} />
                        </div>
                        <div>
                          <h3 className='text-2xl font-black text-white'>Booking Overview</h3>
                          <p className='text-white/40 font-medium'>Detailed financial summary of your property</p>
                        </div>
                      </div>

                      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8'>
                        {[
                          { label: 'Total Plot Value', value: `Rs. ${(totals[selectedPlot._id]?.totalPlotAmount || 0).toLocaleString()}`, icon: Home, color: 'text-white' },
                          { label: 'Initial Deposit', value: `Rs. ${(totals[selectedPlot._id]?.initialPayment || 0).toLocaleString()}`, icon: CreditCard, color: 'text-blue-400' },
                          { label: 'Total Paid', value: `Rs. ${(totals[selectedPlot._id]?.totalPaidAmount || 0).toLocaleString()}`, icon: CheckCircle2, color: 'text-emerald-400' },
                          { label: 'Remaining Balance', value: `Rs. ${(totals[selectedPlot._id]?.totalPendingInstallmentsAmount || 0).toLocaleString()}`, icon: Clock, color: 'text-amber-400' },
                        ].map((stat, i) => (
                          <div key={i} className="space-y-3">
                            <div className="flex items-center gap-2 text-white/30">
                              <stat.icon size={16} />
                              <span className="text-[10px] font-black uppercase tracking-widest">{stat.label}</span>
                            </div>
                            <p className={`text-2xl font-black ${stat.color}`}>{stat.value}</p>
                          </div>
                        ))}
                      </div>

                      <div className="mt-10 pt-10 border-t border-white/5 grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-widest text-white/30 mb-2">Payment Progress</p>
                          <div className="flex items-center gap-4">
                            <div className="flex-1 h-3 bg-white/5 rounded-full overflow-hidden border border-white/5">
                              <motion.div
                                className="h-full bg-gradient-to-r from-amber-500 to-orange-500"
                                initial={{ width: 0 }}
                                animate={{ width: `${completionPercentage}%` }}
                                transition={{ duration: 1.5, ease: "easeOut" }}
                              />
                            </div>
                            <span className="text-xl font-black text-white">{Math.round(completionPercentage)}%</span>
                          </div>
                        </div>
                        <div className="flex flex-col justify-center">
                          <p className="text-[10px] font-black uppercase tracking-widest text-white/30 mb-1">Booking Date</p>
                          <p className="text-lg font-bold text-white">
                            {totals[selectedPlot._id]?.bookingDate ? new Date(totals[selectedPlot._id]?.bookingDate).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' }) : 'N/A'}
                          </p>
                        </div>
                        <div className="flex flex-col justify-center">
                          <p className="text-[10px] font-black uppercase tracking-widest text-white/30 mb-1">Property Category</p>
                          <p className="text-lg font-bold text-white uppercase tracking-tight">{totals[selectedPlot._id]?.category || 'General'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* SECTION 3: Payment Schedule Table */}
                  {paymentSchedule.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`${theme.tailwind.BgBottom6} backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl`}
                    >
                      <div className='flex items-center gap-4 mb-6'>
                        <div className="p-3 rounded-2xl bg-blue-500/10 text-blue-500">
                          <Calendar size={24} />
                        </div>
                        <div>
                          <h3 className='text-2xl font-black text-white'>Payment Schedule</h3>
                          <p className='text-white/40 font-medium'>Complete payment timeline</p>
                        </div>
                      </div>
                      <div className='overflow-x-auto custom-scrollbar'>
                        <table className='w-full'>
                          <thead>
                            <tr className='bg-white/5'>
                              <th className='px-6 py-4 text-left text-[10px] font-black uppercase tracking-widest text-white/30'>#</th>
                              <th className='px-6 py-4 text-left text-[10px] font-black uppercase tracking-widest text-white/30'>Due Date</th>
                              <th className='px-6 py-4 text-left text-[10px] font-black uppercase tracking-widest text-white/30'>Amount</th>
                              <th className='px-6 py-4 text-left text-[10px] font-black uppercase tracking-widest text-white/30'>Paid</th>
                              <th className='px-6 py-4 text-left text-[10px] font-black uppercase tracking-widest text-white/30'>Remaining</th>
                              <th className='px-6 py-4 text-left text-[10px] font-black uppercase tracking-widest text-white/30'>Payment Method</th>
                              <th className='px-6 py-4 text-left text-[10px] font-black uppercase tracking-widest text-white/30'>Status</th>
                            </tr>
                          </thead>
                          <tbody className='divide-y divide-white/5'>
                            {paymentSchedule
                              .filter(inst => {
                                const plotId = (inst.plot_id?._id || inst.plot_id)?.toString();
                                return plotId === selectedPlot?._id?.toString();
                              })
                              .slice(0, 10)
                              .map((inst, index) => {
                                const remaining = Math.max(0, (inst.amount || 0) - (inst.paid_amount || 0));
                                const dueDate = inst.due_date ? new Date(inst.due_date) : null;
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                const isOverdue = dueDate && dueDate < today && remaining > 0;
                                const isUpcoming = dueDate && dueDate >= today && remaining > 0;
                                
                                return (
                                  <tr key={inst._id} className='hover:bg-white/5 transition-colors'>
                                    <td className='px-6 py-4'>
                                      <span className='text-sm font-bold text-white/60'>{index + 1}</span>
                                    </td>
                                    <td className='px-6 py-4'>
                                      <span className='text-sm font-medium text-white'>
                                        {inst.due_date ? new Date(inst.due_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
                                      </span>
                                    </td>
                                    <td className='px-6 py-4'>
                                      <span className='text-sm font-bold text-white'>
                                        Rs. {(inst.amount || 0).toLocaleString()}
                                      </span>
                                    </td>
                                    <td className='px-6 py-4'>
                                      <span className='text-sm font-medium text-emerald-400'>
                                        Rs. {(inst.paid_amount || 0).toLocaleString()}
                                      </span>
                                    </td>
                                    <td className='px-6 py-4'>
                                      <span className={`text-sm font-bold ${isOverdue ? 'text-red-400' : 'text-white'}`}>
                                        Rs. {remaining.toLocaleString()}
                                      </span>
                                    </td>
                                    <td className='px-6 py-4'>
                                      <span className='text-sm font-medium text-white/60'>
                                        {inst.payment_method || 'N/A'}
                                      </span>
                                    </td>
                                    <td className='px-6 py-4'>
                                      <span className={`px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-widest ${
                                        inst.status === 'Completed' 
                                          ? 'bg-emerald-500/20 text-emerald-400'
                                          : isOverdue
                                          ? 'bg-red-500/20 text-red-400'
                                          : isUpcoming
                                          ? 'bg-amber-500/20 text-amber-400'
                                          : 'bg-blue-500/20 text-blue-400'
                                      }`}>
                                        {inst.status}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    </motion.div>
                  )}

                  {/* SECTION 4: Documents Section */}
                  {selectedPlotBooking && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`${theme.tailwind.BgBottom6} backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-2xl`}
                    >
                      <div className='flex items-center gap-4 mb-6'>
                        <div className="p-3 rounded-2xl bg-purple-500/10 text-purple-500">
                          <FileText size={24} />
                        </div>
                        <div>
                          <h3 className='text-2xl font-black text-white'>Documents</h3>
                          <p className='text-white/40 font-medium'>Download agreements, receipts, and upload documents</p>
                        </div>
                      </div>
                      <div className='space-y-4'>
                        <div className='flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10'>
                          <div className='flex items-center gap-3'>
                            <FileText size={20} className='text-emerald-400' />
                            <span className='text-white font-medium'>Booking Agreement</span>
                          </div>
                          <button
                            onClick={async () => {
                              if (selectedPlotBooking?._id) {
                                try {
                                  await downloadDocumentPDF(selectedPlotBooking._id, 'agreement');
                                } catch (error) {
                                  console.error('Error downloading agreement:', error);
                                  const errorMsg = error?.message || error?.response?.data?.message || 'Failed to download agreement. Please try again.';
                                  alert(errorMsg);
                                }
                              } else {
                                alert('Please select a booking first.');
                              }
                            }}
                            className='px-4 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 rounded-lg transition-colors text-sm font-bold'
                          >
                            Download PDF
                          </button>
                        </div>
                        <div className='flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10'>
                          <div className='flex items-center gap-3'>
                            <FileText size={20} className='text-blue-400' />
                            <span className='text-white font-medium'>Allotment Letter</span>
                          </div>
                          <button
                            onClick={async () => {
                              if (selectedPlotBooking?._id) {
                                try {
                                  await downloadDocumentPDF(selectedPlotBooking._id, 'allotment');
                                } catch (error) {
                                  console.error('Error downloading allotment letter:', error);
                                  const errorMsg = error?.message || error?.response?.data?.message || 'Failed to download allotment letter. Please try again.';
                                  alert(errorMsg);
                                }
                              } else {
                                alert('Please select a booking first.');
                              }
                            }}
                            className='px-4 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-lg transition-colors text-sm font-bold'
                          >
                            Download PDF
                          </button>
                        </div>
                        <div className='flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10'>
                          <div className='flex items-center gap-3'>
                            <ReceiptText size={20} className='text-purple-400' />
                            <span className='text-white font-medium'>
                              Payment Receipts ({completeInstallments.length} receipts)
                            </span>
                          </div>
                          <button
                            onClick={async () => {
                              if (completeInstallments.length === 0) {
                                alert('No receipts available to download.');
                                return;
                              }
                              
                              // Confirm with user
                              const confirmDownload = window.confirm(
                                `Download all ${completeInstallments.length} receipt(s)?\n\n` +
                                `⚠️ IMPORTANT:\n` +
                                `- Your browser may ask for permission to download multiple files. Please click "Allow" or "Keep" for each download.\n` +
                                `- Downloads will happen one at a time with a 2-second delay between each.\n` +
                                `- This may take ${Math.ceil(completeInstallments.length * 2.5)} seconds.`
                              );
                              
                              if (!confirmDownload) return;
                              
                              setDownloadingAllReceipts(true);
                              setDownloadProgress({ current: 0, total: completeInstallments.length });
                              
                              let successCount = 0;
                              let failCount = 0;
                              const failedReceipts = [];
                              
                              try {
                                // Download all receipts with longer delays to prevent browser blocking
                                for (let i = 0; i < completeInstallments.length; i++) {
                                  const inst = completeInstallments[i];
                                  setDownloadProgress({ current: i + 1, total: completeInstallments.length });
                                  
                                  try {
                                    console.log(`[${i + 1}/${completeInstallments.length}] Downloading receipt: ${inst.receipt_no || inst._id}`);
                                    await downloadReceiptPDF(inst._id, inst.receipt_no);
                                    successCount++;
                                    console.log(`✓ [${i + 1}/${completeInstallments.length}] Successfully downloaded receipt ${inst.receipt_no || inst._id}`);
                                    
                                    // Wait longer between downloads (2.5 seconds) to prevent browser blocking
                                    // Browsers often block multiple rapid downloads
                                    if (i < completeInstallments.length - 1) {
                                      await new Promise(resolve => setTimeout(resolve, 2500));
                                    }
                                  } catch (error) {
                                    console.error(`✗ [${i + 1}/${completeInstallments.length}] Error downloading receipt ${inst.receipt_no || inst._id}:`, error);
                                    failCount++;
                                    failedReceipts.push({
                                      receiptNo: inst.receipt_no || 'N/A',
                                      installmentId: inst._id,
                                      error: error.message || 'Unknown error'
                                    });
                                    
                                    // Still wait even on error to maintain timing
                                    if (i < completeInstallments.length - 1) {
                                      await new Promise(resolve => setTimeout(resolve, 2500));
                                    }
                                  }
                                }
                                
                                // Show detailed summary
                                if (failCount === 0) {
                                  alert(`✅ Successfully downloaded all ${successCount} receipt(s)!`);
                                } else {
                                  const failedList = failedReceipts.slice(0, 5).map(f => `  • Receipt ${f.receiptNo}`).join('\n');
                                  const moreFailed = failedReceipts.length > 5 ? `\n  ... and ${failedReceipts.length - 5} more` : '';
                                  alert(
                                    `⚠️ Download Summary:\n\n` +
                                    `✅ Successfully downloaded: ${successCount} receipt(s)\n` +
                                    `❌ Failed: ${failCount} receipt(s)\n\n` +
                                    `Failed receipts:\n${failedList}${moreFailed}\n\n` +
                                    `Please try downloading the failed receipts individually using the PDF button next to each receipt.`
                                  );
                                }
                              } finally {
                                setDownloadingAllReceipts(false);
                                setDownloadProgress({ current: 0, total: 0 });
                              }
                            }}
                            disabled={completeInstallments.length === 0 || downloadingAllReceipts}
                            className='px-4 py-2 bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 rounded-lg transition-colors text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2'
                          >
                            {downloadingAllReceipts ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-400"></div>
                                <span>Downloading ({downloadProgress.current}/{downloadProgress.total})...</span>
                              </>
                            ) : (
                              <span>Download All ({completeInstallments.length})</span>
                            )}
                          </button>
                        </div>
                        
                        {/* P1: Document Upload Section */}
                        <div className='mt-6 pt-6 border-t border-white/10'>
                          <h4 className='text-lg font-bold text-white mb-4'>Upload Documents</h4>
                          <p className='text-sm text-white/60 mb-4'>Upload CNIC, agreement forms, or other supporting documents (PDF, JPG, PNG - Max 5MB each)</p>
                          <div className='space-y-4'>
                            <input
                              type="file"
                              multiple
                              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                              onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                              className="w-full text-white/70 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-500/10 file:text-blue-400 hover:file:bg-blue-500/20 file:cursor-pointer"
                            />
                            {selectedFiles.length > 0 && (
                              <div className='text-sm text-white/60'>
                                {selectedFiles.length} file(s) selected
                              </div>
                            )}
                            <button
                              onClick={async () => {
                                if (selectedFiles.length === 0 || !selectedPlotBooking?._id) {
                                  alert('Please select files and ensure a booking is selected.');
                                  return;
                                }
                                setUploadingDocuments(true);
                                try {
                                  await uploadCustomerDocuments(selectedPlotBooking._id, selectedFiles);
                                  alert('Documents uploaded successfully!');
                                  setSelectedFiles([]);
                                  // Optionally refetch customer documents to update UI
                                  if (selectedPlotBooking._id) {
                                    try {
                                      const docs = await fetchCustomerDocuments(selectedPlotBooking._id);
                                      setCustomerDocuments(docs);
                                    } catch (error) {
                                      console.error('Error fetching customer documents:', error);
                                    }
                                  }
                                } catch (error) {
                                  console.error('Error uploading documents:', error);
                                  const errorMsg = error?.response?.data?.message || error?.message || 'Failed to upload documents. Please try again.';
                                  alert(errorMsg);
                                } finally {
                                  setUploadingDocuments(false);
                                }
                              }}
                              disabled={uploadingDocuments || selectedFiles.length === 0}
                              className='flex items-center justify-center gap-3 px-6 py-3 rounded-xl bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 font-bold transition-all duration-300 border border-blue-500/20 hover:border-blue-500/40 shadow-lg hover:shadow-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed'
                            >
                              {uploadingDocuments ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
                                  <span>Uploading...</span>
                                </>
                              ) : (
                                <>
                                  <FileText size={20} />
                                  <span>Upload Selected Documents</span>
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </section>
              )}

              {selectedPlot && (
                <div className='flex-1 mt-12 space-y-8 animate-fadeIn'>
                  <div className='space-y-8'>
                    {(currentView === 'all' || currentView === 'pending') && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4 }}
                        className='bg-white/5 backdrop-blur-xl rounded-3xl overflow-hidden border border-white/10 shadow-2xl space-y-0'
                      >
                        <div className='p-8 bg-gradient-to-r from-amber-500/10 via-transparent to-transparent'>
                          <div className='flex items-center justify-between flex-wrap gap-4'>
                            <div className='flex items-center gap-4'>
                              <div className='p-3 rounded-2xl bg-amber-500/20 text-amber-500'>
                                <Clock size={24} />
                              </div>
                              <div>
                                <h3 className='text-xl font-bold text-white'>Pending Installments</h3>
                                <p className='text-white/40 text-sm font-medium'>{filteredInstallments.length} payments outstanding</p>
                              </div>
                            </div>
                            <div className='bg-amber-500/10 border border-amber-500/20 px-4 py-2 rounded-xl'>
                              <p className='text-[10px] font-black uppercase tracking-widest text-amber-500/60 mb-1'>Total Pending</p>
                              <p className='text-lg font-black text-amber-500 uppercase'>
                                Rs. {(totals[selectedPlot._id]?.totalPendingInstallmentsAmount || 0).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className='overflow-x-auto custom-scrollbar'>
                          <table className='w-full'>
                            <thead>
                              <tr className='bg-white/5'>
                                <th className='px-8 py-4 text-left text-[10px] font-black uppercase tracking-widest text-white/30'>ID</th>
                                <th className='px-8 py-4 text-left text-[10px] font-black uppercase tracking-widest text-white/30'>Installment Detail</th>
                                <th className='px-8 py-4 text-left text-[10px] font-black uppercase tracking-widest text-white/30'>Due Date</th>
                                <th className='px-8 py-4 text-left text-[10px] font-black uppercase tracking-widest text-white/30'>Amount</th>
                                <th className='px-8 py-4 text-left text-[10px] font-black uppercase tracking-widest text-white/30'>Status</th>
                                <th className='px-8 py-4 text-right text-[10px] font-black uppercase tracking-widest text-white/30'>Action</th>
                              </tr>
                            </thead>
                            <tbody className='divide-y divide-white/5'>
                              {filteredInstallments.length > 0 ? (
                                filteredInstallments.map((installment, index) => (
                                  <tr key={installment._id} className='hover:bg-white/5 transition-colors group'>
                                    <td className='px-8 py-6'>
                                      <span className='text-xs font-black text-white/20 group-hover:text-amber-500/40 transition-colors'>#{index + 1}</span>
                                    </td>
                                    <td className='px-8 py-6'>
                                      <div className='flex items-center gap-3'>
                                        <div className={`p-2 rounded-lg ${installment.status === 'Overdue' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/10 text-amber-500'
                                          }`}>
                                          {installment.status === 'Overdue' ? <AlertTriangle size={16} /> : <Calendar size={16} />}
                                        </div>
                                        <div className='flex-1'>
                                          <div className='flex items-center gap-2 flex-wrap'>
                                            <p className='text-sm font-bold text-white'>Installment {installment.installment_number}</p>
                                            {installment.status === 'Transferred' && installment.transfer_type === 'resell' && (
                                              <span className='px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest bg-blue-500/20 text-blue-400 border border-blue-500/30'>
                                                Paid by Previous Customer
                                              </span>
                                            )}
                                          </div>
                                          <p className='text-[10px] text-white/30 uppercase tracking-tight font-black mt-1'>
                                            {installment.status === 'Overdue' ? 'Critical Action Required' : 
                                             installment.status === 'Transferred' && installment.transfer_type === 'resell' && installment.previous_customer_id?.name 
                                               ? `Previously paid by ${installment.previous_customer_id.name}` 
                                               : 'Standard Payment'}
                                          </p>
                                        </div>
                                      </div>
                                    </td>
                                    <td className='px-8 py-6'>
                                      <p className='text-sm font-medium text-white/60'>{new Date(installment.due_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                                    </td>
                                    <td className='px-8 py-6'>
                                      <p className='text-sm font-black text-white'>
                                        Rs. {Math.max(0, (installment.amount || 0) - (installment.paid_amount || 0)).toLocaleString()}
                                      </p>
                                    </td>
                                    <td className='px-8 py-6'>
                                      <span className={`px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-widest ${installment.status === 'Overdue' ? 'bg-red-500/20 text-red-500' : 'bg-amber-500/10 text-amber-500'
                                        }`}>
                                        {installment.status}
                                      </span>
                                    </td>
                                    <td className='px-8 py-6 text-right'>
                                      <button className='px-4 py-2 bg-amber-500/10 hover:bg-amber-500 text-amber-500 hover:text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all duration-300 border border-amber-500/20'>
                                        Pay Online
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={6} className='px-8 py-20 text-center'>
                                    <div className='flex flex-col items-center gap-4'>
                                      <div className='p-6 rounded-full bg-emerald-500/10 text-emerald-500'>
                                        <CheckCircle size={40} />
                                      </div>
                                      <div>
                                        <p className='text-xl font-black text-white'>All Caught Up!</p>
                                        <p className='text-white/40 font-medium'>You have no pending installments for this plot.</p>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </motion.div>
                    )}

                    {(currentView === 'all' || currentView === 'completed') && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                        className='bg-white/5 backdrop-blur-xl rounded-3xl overflow-hidden border border-white/10 shadow-2xl space-y-0'
                      >
                        <div className='p-8 bg-gradient-to-r from-emerald-500/10 via-transparent to-transparent'>
                          <div className='flex items-center justify-between flex-wrap gap-4'>
                            <div className='flex items-center gap-4'>
                              <div className='p-3 rounded-2xl bg-emerald-500/20 text-emerald-500'>
                                <CheckCircle2 size={24} />
                              </div>
                              <div>
                                <h3 className='text-xl font-bold text-white'>Completed Payments</h3>
                                <p className='text-white/40 text-sm font-medium'>{completeInstallments.length} installments successfully paid</p>
                              </div>
                            </div>
                            <div className='flex items-center gap-4'>
                              <div className='bg-emerald-500/10 border border-emerald-500/20 px-4 py-2 rounded-xl'>
                                <p className='text-[10px] font-black uppercase tracking-widest text-emerald-500/60 mb-1'>Total Paid</p>
                                <p className='text-lg font-black text-emerald-400 uppercase'>
                                  Rs. {(totals[selectedPlot._id]?.totalPaidAmount || 0).toLocaleString()}
                                </p>
                              </div>
                              {completeInstallments.length > 0 && (
                                <button
                                  onClick={() => setIsCompletedInstallmentsPrintModalOpen(true)}
                                  className='p-3 bg-white/5 hover:bg-white/10 text-white rounded-xl transition-all border border-white/10'
                                  title="Print Payment Report"
                                >
                                  <Printer size={20} />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className='overflow-x-auto custom-scrollbar'>
                          <table className='w-full'>
                            <thead>
                              <tr className='bg-white/5'>
                                <th className='px-8 py-4 text-left text-[10px] font-black uppercase tracking-widest text-white/30'>Ref</th>
                                <th className='px-8 py-4 text-left text-[10px] font-black uppercase tracking-widest text-white/30'>Payment Information</th>
                                <th className='px-8 py-4 text-left text-[10px] font-black uppercase tracking-widest text-white/30'>Receipt #</th>
                                <th className='px-8 py-4 text-left text-[10px] font-black uppercase tracking-widest text-white/30'>Amount Paid</th>
                                <th className='px-8 py-4 text-left text-[10px] font-black uppercase tracking-widest text-white/30'>Clearance Date</th>
                                <th className='px-8 py-4 text-right text-[10px] font-black uppercase tracking-widest text-white/30'>Documents</th>
                              </tr>
                            </thead>
                            <tbody className='divide-y divide-white/5'>
                              {completeInstallments.length > 0 ? (
                                completeInstallments.map((installment, index) => (
                                  <tr key={installment._id} className='hover:bg-white/5 transition-colors group'>
                                    <td className='px-8 py-6'>
                                      <span className='text-xs font-black text-white/20 group-hover:text-emerald-500/40 transition-colors'>#{index + 1}</span>
                                    </td>
                                    <td className='px-8 py-6'>
                                      <div className='flex items-center gap-3'>
                                        <div className='p-2 rounded-lg bg-emerald-500/10 text-emerald-500'>
                                          <CheckCircle size={16} />
                                        </div>
                                        <div className='flex-1'>
                                          <div className='flex items-center gap-2 flex-wrap'>
                                            <p className='text-sm font-bold text-white'>Installment {installment.installment_number}</p>
                                            {installment.status === 'Transferred' && installment.transfer_type === 'resell' && (
                                              <span className='px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest bg-blue-500/20 text-blue-400 border border-blue-500/30'>
                                                Paid by Previous Customer
                                              </span>
                                            )}
                                          </div>
                                          <p className='text-[10px] text-emerald-500/60 uppercase tracking-tight font-black mt-1'>
                                            {installment.status === 'Transferred' && installment.transfer_type === 'resell' && installment.previous_customer_id?.name 
                                              ? `Previously paid by ${installment.previous_customer_id.name}` 
                                              : 'Verified Payment'}
                                          </p>
                                        </div>
                                      </div>
                                    </td>
                                    <td className='px-8 py-6'>
                                      <span className='text-xs font-black text-white/60 bg-white/5 px-2 py-1 rounded border border-white/10'>REC-{installment.receipt_no}</span>
                                    </td>
                                    <td className='px-8 py-6'>
                                      <p className='text-sm font-black text-emerald-400'>Rs. {installment.paid_amount?.toLocaleString()}</p>
                                    </td>
                                    <td className='px-8 py-6'>
                                      <div className='flex items-center gap-2 text-white/60'>
                                        <Calendar size={14} />
                                        <span className='text-sm font-medium'>{installment.payment_date ? new Date(installment.payment_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}</span>
                                      </div>
                                    </td>
                                    <td className='px-8 py-6 text-right'>
                                      <div className='flex items-center gap-2 justify-end'>
                                        <button
                                          onClick={() => handleViewSlip(installment)}
                                          className='px-4 py-2 bg-white/5 hover:bg-emerald-500/20 text-white hover:text-emerald-400 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all duration-300 border border-white/10 group-hover:border-emerald-500/30 flex items-center gap-2'
                                        >
                                          <ReceiptText size={14} />
                                          <span>View</span>
                                        </button>
                                        <button
                                          onClick={async () => {
                                            try {
                                              await downloadReceiptPDF(installment._id, installment.receipt_no);
                                            } catch (error) {
                                              console.error('Error downloading receipt:', error);
                                              const errorMsg = error?.message || 'Failed to download receipt. Please try again.';
                                              alert(errorMsg);
                                            }
                                          }}
                                          className='px-4 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 hover:text-blue-300 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all duration-300 border border-blue-500/20 flex items-center gap-2'
                                        >
                                          <Printer size={14} />
                                          <span>PDF</span>
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan={6} className='px-8 py-20 text-center'>
                                    <div className='flex flex-col items-center gap-4 text-white/20'>
                                      <HandCoins size={48} />
                                      <p className='text-sm font-black uppercase tracking-widest'>No completed payments found</p>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </motion.div>
                    )}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals & Global Styles */}
      < Modal
        isOpen={isCompletedInstallmentsPrintModalOpen}
        onRequestClose={() => setIsCompletedInstallmentsPrintModalOpen(false)}
        style={{
          overlay: modalOverlayStyle,
          content: modalStyle
        }}
      >
        <div className='relative h-full flex flex-col overflow-hidden'>
          <button
            onClick={() => setIsCompletedInstallmentsPrintModalOpen(false)}
            className='absolute right-4 top-4 p-2 bg-white/20 hover:bg-white/30 text-white rounded-full transition-colors z-50'
          >
            <X size={20} />
          </button>
          {selectedPlot && (
            <CompletedInstallmentsPrint
              installments={allInstallments.filter(
                installment =>
                  ((installment.plot_id?._id || installment.plot_id)?.toString() === selectedPlot?._id?.toString())
              )}
              totalPaidAmount={totals[selectedPlot?._id]?.totalPaidAmount || 0}
              totalPlotAmount={totals[selectedPlot?._id]?.totalPlotAmount || 0}
              initialPayment={totals[selectedPlot?._id]?.initialPayment || 0}
              companyInfo={selectedPlot}
            />
          )}
        </div>
      </Modal >

      <style>{printStyles}</style>

      <Modal
        isOpen={isSlipModalOpen}
        onRequestClose={handleCloseModal}
        style={{ overlay: modalOverlayStyle, content: modalStyle }}
      >
        <button
          onClick={handleCloseModal}
          className='absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full'
        >
          <X size={20} />
        </button>
        <InstallmentBookingSlip
          installment={selectedInstallment}
          companyInfo={selectedPlot}
          payment={totals}
        />
      </Modal>
    </div >
  )
}

export default PaidInstallments
